import { useEffect, useState } from "react";
import { LOGOS } from "../lib/logos";

/**
 * Splash — cinematic opening logo intro.
 *
 * Only the SoulTrip painted wordmark (transparent PNG) is rendered.
 * No tagline. No eagle. No box. No background panel.
 *
 * Sequence (total ~4.4s):
 *   0.0–0.9s  → black overlay + logo fades in at centre, scaled up
 *   0.9–2.8s  → hold centred
 *   2.8–3.8s  → logo flies to top-left nav corner and scales down
 *   3.8–4.4s  → splash overlay fades out, nav logo takes over
 *
 * Broadcasts `soultrip:splash-done` so <Nav> reveals its wordmark seamlessly.
 */
export const Splash = ({ onDone }) => {
  const [stage, setStage] = useState(0); // 0 mount, 1 fly, 2 fade out, 3 gone
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 640px)").matches
      : false
  );

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const mq = window.matchMedia("(max-width: 640px)");
    const onMq = (e) => setIsMobile(e.matches);
    mq.addEventListener?.("change", onMq);

    const t1 = setTimeout(() => setStage(1), 2800);
    const t2 = setTimeout(() => {
      setStage(2);
      window.dispatchEvent(new CustomEvent("soultrip:splash-done"));
    }, 3800);
    const t3 = setTimeout(() => {
      setStage(3);
      document.body.style.overflow = prevOverflow;
      onDone?.();
    }, 4400);

    return () => {
      [t1, t2, t3].forEach(clearTimeout);
      mq.removeEventListener?.("change", onMq);
      document.body.style.overflow = prevOverflow;
    };
  }, [onDone]);

  if (stage === 3) return null;

  // Final resting position = where the nav wordmark lives.
  // Nav padding: px-6 md:px-12 / py-3 md:py-4. Wordmark height must match Nav.
  const navLeft = isMobile ? 24 : 48;
  const navTop = isMobile ? 12 : 16;
  const navLogoHeight = isMobile ? 52 : 76;
  const initialScale = isMobile ? 2.2 : 3.0;

  const atRest = stage >= 1;

  return (
    <div
      data-testid="splash-overlay"
      aria-hidden={stage >= 2}
      className="fixed inset-0 z-[9999] pointer-events-none"
      style={{
        background: "var(--coal)",
        opacity: stage < 2 ? 1 : 0,
        transition: "opacity .6s ease",
      }}
    >
      {/* Subtle radial glow behind the logo */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 50% 40% at 50% 50%, rgba(201,160,74,.18) 0%, transparent 70%)",
          opacity: stage === 0 ? 1 : 0.2,
          transition: "opacity 1s ease",
        }}
      />

      {/* Splash logo — animates from centre to nav corner.
          Rendered bare: no wrapper, no box, no background. */}
      <img
        src={LOGOS.soulTripMark}
        alt="Soul Trip"
        data-testid="splash-logo"
        draggable={false}
        style={{
          position: "fixed",
          top: atRest ? `${navTop}px` : "50%",
          left: atRest ? `${navLeft}px` : "50%",
          height: `${navLogoHeight}px`,
          width: "auto",
          objectFit: "contain",
          background: "transparent",
          transform: atRest
            ? "translate(0, 0) scale(1)"
            : `translate(-50%, -50%) scale(${initialScale})`,
          opacity: stage === 0 ? 0 : stage >= 2 ? 0 : 1,
          filter:
            "drop-shadow(0 8px 38px rgba(201,160,74,.45)) drop-shadow(0 4px 18px rgba(0,0,0,.85))",
          transition: atRest
            ? "top 1.1s cubic-bezier(.65,.05,.36,1), left 1.1s cubic-bezier(.65,.05,.36,1), transform 1.1s cubic-bezier(.65,.05,.36,1), opacity .6s ease .6s"
            : "opacity 1.0s cubic-bezier(.2,.7,.2,1)",
          animation: stage === 0 ? "splashFadeIn 1.0s .2s both" : undefined,
        }}
      />

      <style>{`
        @keyframes splashFadeIn {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};
